import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import toast from 'react-hot-toast';
import { TrendingUp, DollarSign, Clock, Download, Users } from 'lucide-react';
import { formatDateOnly } from '../../lib/utils';

const RelatoriosVisaoGeral = () => {
  const [stats, setStats] = useState(null);
  const [produtividade, setProdutividade] = useState({ por_tecnico: [] });
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [periodo, setPeriodo] = useState({
    data_inicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
  });
  const [clienteId, setClienteId] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clientesRes, tecnicosRes] = await Promise.all([
          api.get('/clientes', { params: { limit: 500 } }),
          api.get('/users', { params: { funcao: 'tecnico' } }),
        ]);
        setClientes(clientesRes.data?.clientes || []);
        setTecnicos(tecnicosRes.data?.users || []);
      } catch {
        // opções de filtro falham em silêncio
      }
    };
    loadOptions();
  }, []);

  useEffect(() => {
    const loadRelatorios = async () => {
      try {
        const params = { ...periodo };
        if (clienteId) params.cliente_id = clienteId;
        if (tecnicoId) params.tecnico_id = tecnicoId;
        const [dashboardRes, prodRes] = await Promise.all([
          api.get('/relatorios/dashboard', { params }),
          api.get('/relatorios/produtividade', {
            params: {
              data_inicio: periodo.data_inicio,
              data_fim: periodo.data_fim,
              tecnico_id: tecnicoId || undefined,
            },
          }),
        ]);
        setStats(dashboardRes.data);
        setProdutividade(prodRes.data || { por_tecnico: [] });
      } catch (error) {
        toast.error('Erro ao carregar relatórios');
      } finally {
        setLoading(false);
      }
    };
    loadRelatorios();
  }, [periodo, clienteId, tecnicoId]);

  const exportar = async (formato) => {
    try {
      const params = { data_inicio: periodo.data_inicio, data_fim: periodo.data_fim, formato };
      const res = await api.get('/relatorios/dashboard/exportar', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_${periodo.data_inicio}_${periodo.data_fim}.${formato === 'xlsx' ? 'xlsx' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch (err) {
      toast.error('Erro ao exportar');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end flex-wrap gap-2">
        {stats && (
          <>
            <button
              onClick={() => exportar('xlsx')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            >
              <Download className="w-4 h-4" /> Exportar Excel
            </button>
            <button
              onClick={() => exportar('csv')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
            >
              <Download className="w-4 h-4" /> Exportar CSV
            </button>
          </>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data Início</label>
            <input
              type="date"
              value={periodo.data_inicio}
              onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={periodo.data_fim}
              onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cliente</label>
            <select
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome || c.codigo || c.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Técnico</label>
            <select
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none bg-white"
            >
              <option value="">Todos</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.email || t.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-sm font-medium text-slate-600">Total Rotas</h3>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_rotas}</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-sm font-medium text-slate-600">KM Percorridos</h3>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_km.toFixed(0)} km</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-sm font-medium text-slate-600">Custo Total</h3>
              <p className="text-2xl font-bold text-slate-900 mt-1">R$ {stats.total_custo.toFixed(2)}</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-sm font-medium text-slate-600">Horas Extras</h3>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_horas_extras.toFixed(1)}h</p>
            </div>
          </div>

          {produtividade.por_tecnico && produtividade.por_tecnico.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" /> Produtividade por técnico
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Rotas</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Visitas realizadas</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Média visitas/rota</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">KM</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">H. Extras</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtividade.por_tecnico.map((row) => (
                      <tr key={row.tecnico_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium text-slate-900">{row.tecnico_nome}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">{row.total_rotas}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">{row.total_visitas_realizadas}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-600">{row.media_visitas_por_rota}</td>
                        <td className="py-3 px-4 text-sm text-right text-slate-900">{Number(row.total_km || 0).toFixed(0)} km</td>
                        <td className="py-3 px-4 text-sm text-right text-orange-600">{Number(row.total_horas_extras || 0).toFixed(1)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Rotas do Período</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Carro</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">KM</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Custo</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">H. Extras</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.rotas.map((rota) => (
                    <tr key={rota.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900">{formatDateOnly(rota.data)}</td>
                      <td className="py-3 px-4 text-sm text-slate-900">{rota.tecnico_nome}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{rota.carro_placa}</td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900">{rota.km_total.toFixed(0)} km</td>
                      <td className="py-3 px-4 text-sm font-medium text-emerald-600">R$ {rota.custo_total.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-orange-600">{rota.horas_extras.toFixed(1)}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RelatoriosVisaoGeral;
